import { Helmet } from "react-helmet";
import PageTitle from "../../components/PageTitle";
import { Settings } from "../../configuration/settings";
import Item from "../../models/item";
import RecomendedItems from "../../components/RecomendedItems";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import ModelLinks from "../../components/ModelLinks";
import { ModelDetails } from "../../configuration/model-description";
import Faq from "../../components/Faq";

function SizesTable(props: { items: Item[] }) {
    const topModels = Object.entries(ModelDetails)
        .sort(([, modelDetailsA], [, modelDetailsB]) => modelDetailsA.seoRank - modelDetailsB.seoRank)
        .map(([model, modelDetails]) => model)
        .slice(0, 4)
        .sort();

    return (
        <>
            <Helmet>
                <title>Розмірна сітка джинсів Levi’s {topModels.join(", ")} та інших: як обрати розмір | {Settings.shopName}</title>
                <meta name="description" content={`Розмірна сітка джинсів Levi’s: що означають W і L, як правильно зняти заміри та обрати розмір джинсів Levi’s ${topModels.join(", ")} та інших. Поради від ${Settings.shopName}.`} />
                <link rel="canonical" href={`${Settings.siteUrl}/tablytsia-rozmiriv-levis/`} />
            </Helmet>
            <Breadcrumbs items={[ { text: "Розмірна сітка джинсів Levi’s"} ]} />
            <PageTitle title="Розмірна сітка джинсів Levi’s: як правильно обрати свій розмір" />
            <div className="text-start">
                <p>
                    Правильно підібраний розмір - основа комфорту та ідеальної посадки.
                    Особливо це важливо при покупці онлайн.
                    У цій статті ми пояснюємо, як працює <strong>розмірна сітка джинсів Levi’s</strong>,
                    що означають позначення W та L і як обрати джинси без помилок.
                </p>

                <h2 className="fs-5">Що означають W та L у джинсах Levi’s</h2>
                <p>
                    У джинсах Levi’s використовується американська система розмірів,
                    яка вказується у форматі <strong>W - L</strong>:
                </p>

                <ul>
                    <li><strong>W (Waist)</strong> - обхват талії в дюймах</li>
                    <li><strong>L (Length)</strong> - довжина штанини по внутрішньому шву в дюймах</li>
                </ul>

                <h2 className="fs-5">Як правильно знімати мірки</h2>
                <ul>
                    <li>
                        <strong>Обхват талії</strong> - 
                        Виконайте виміри найвужчої частини вашої талії. Пам'ятайте, що вимірювальна стрічка не повинна бути надто натягнутою.
                    </li>
                    <li>
                        <strong>Обхват стегон</strong> - 
                        Заміряйте найоб'ємнішу частину ваших сідниць, приблизно на рівні кульшових суглобів. Для цього тримайте вимірювальну стрічку горизонтально.
                    </li>
                </ul>

                <h2 className="fs-5">Таблиця розмірів чоловічих джинсів Levi’s</h2>
                <p>
                   Використовуючи таблицю нижче, ви можете підібрати розмір чоловічих джинсів Levi’s. 
                   Основні популярні моделі - {topModels.slice(0, topModels.length - 1).join(", ")} та {topModels[topModels.length - 1]}. 
                   Для інших моделей перегляньте сторінки відповідних джинсів.
                </p>
                <div style={{ height: 400, overflowY: "auto" }}>
                    <table className="table table-striped border">
                        <thead>
                            <tr style={{ position: "sticky", top: 0, backgroundColor: "#fff" }}><th>Розмір виробника (W-L)</th><th>Обхват талії (см)</th><th>Обхват стегон (см)</th><th>Зріст (см)</th></tr>
                        </thead>
                        <tbody>
                            <tr><td>30-30</td><td>77,4 - 78,7</td><td>91,2 - 92,5</td><td>167,6 - 172,7</td></tr>
                            <tr><td>30-32</td><td>77,4 - 78,7</td><td>91,2 - 92,5</td><td>172,7 - 182,8</td></tr>
                            <tr><td>30-34</td><td>77,4 - 78,7</td><td>91,2 - 92,5</td><td>182,8 - 193</td></tr>
                            <tr><td>30-36</td><td>77,4 - 78,7</td><td>91,2 - 92,5</td><td>193 - 198,1</td></tr>
                            <tr><td>31-30</td><td>80 - 81,2</td><td>93,7 - 95</td><td>167,6 - 172,7</td></tr>
                            <tr><td>31-32</td><td>80 - 81,2</td><td>93,7 - 95</td><td>172,7 - 182,8</td></tr>
                            <tr><td>31-34</td><td>80 - 81,2</td><td>93,7 - 95</td><td>182,8 - 193</td></tr>
                            <tr><td>31-36</td><td>80 - 81,2</td><td>93,7 - 95</td><td>193 - 198,1</td></tr>
                            <tr><td>32-30</td><td>82,5 - 83,8</td><td>96,2 - 97,5</td><td>167,6 - 172,7</td></tr>
                            <tr><td>32-32</td><td>82,5 - 83,8</td><td>96,2 - 97,5</td><td>172,7 - 182,8</td></tr>
                            <tr><td>32-34</td><td>82,5 - 83,8</td><td>96,2 - 97,5</td><td>182,8 - 193</td></tr>
                            <tr><td>32-36</td><td>82,5 - 83,8</td><td>96,2 - 97,5</td><td>193 - 198,1</td></tr>
                            <tr><td>33-30</td><td>85 - 86,3</td><td>98,7 - 100</td><td>167,6 - 172,7</td></tr>
                            <tr><td>33-32</td><td>85 - 86,3</td><td>98,7 - 100</td><td>172,7 - 182,8</td></tr>
                            <tr><td>33-34</td><td>85 - 86,3</td><td>98,7 - 100</td><td>182,8 - 193</td></tr>
                            <tr><td>33-36</td><td>85 - 86,3</td><td>98,7 - 100</td><td>193 - 198,1</td></tr>
                            <tr><td>34-30</td><td>87,6 - 88,9</td><td>101 - 102,5</td><td>167,6 - 172,7</td></tr>
                            <tr><td>34-32</td><td>87,6 - 88,9</td><td>101 - 102,5</td><td>172,7 - 182,8</td></tr>
                            <tr><td>34-34</td><td>87,6 - 88,9</td><td>101 - 102,5</td><td>182,8 - 193</td></tr>
                            <tr><td>34-36</td><td>87,6 - 88,9</td><td>101 - 102,5</td><td>193 - 198,1</td></tr>
                            <tr><td>35-30</td><td>90,1 - 91,4</td><td>103 - 105</td><td>167,6 - 172,7</td></tr>
                            <tr><td>35-32</td><td>90,1 - 91,4</td><td>103 - 105</td><td>172,7 - 182,8</td></tr>
                            <tr><td>35-34</td><td>90,1 - 91,4</td><td>103 - 105</td><td>182,8 - 193</td></tr>
                            <tr><td>35-36</td><td>90,1 - 91,4</td><td>103 - 105</td><td>193 - 198,1</td></tr>
                            <tr><td>36-30</td><td>92,7 - 95,2</td><td>106 - 108,7</td><td>167,6 - 172,7</td></tr>
                            <tr><td>36-32</td><td>92,7 - 95,2</td><td>106 - 108,7</td><td>172,7 - 182,8</td></tr>
                            <tr><td>36-34</td><td>92,7 - 95,2</td><td>106 - 108,7</td><td>182,8 - 193</td></tr>
                            <tr><td>36-36</td><td>92,7 - 95,2</td><td>106 - 108,7</td><td>193 - 198,1</td></tr>
                            <tr><td>38-30</td><td>97,7 - 100</td><td>111 - 113,7</td><td>167,6 - 172,7</td></tr>
                            <tr><td>38-32</td><td>97,7 - 100</td><td>111 - 113,7</td><td>172,7 - 182,8</td></tr>
                            <tr><td>38-34</td><td>97,7 - 100</td><td>111 - 113,7</td><td>182,8 - 193</td></tr>
                            <tr><td>38-36</td><td>97,7 - 100</td><td>111 - 113,7</td><td>193 - 198,1</td></tr>
                            <tr><td>40-30</td><td>102 - 105</td><td>116 - 118,7</td><td>167,6 - 172,7</td></tr>
                            <tr><td>40-32</td><td>102 - 105</td><td>116 - 118,7</td><td>172,7 - 182,8</td></tr>
                            <tr><td>40-34</td><td>102 - 105</td><td>116 - 118,7</td><td>182,8 - 193</td></tr>
                            <tr><td>40-36</td><td>102 - 105</td><td>116 - 118,7</td><td>193 - 198,1</td></tr>
                            <tr><td>42-30</td><td>107 - 110</td><td>121 - 123,7</td><td>167,6 - 172,7</td></tr>
                            <tr><td>42-32</td><td>107 - 110</td><td>121 - 123,7</td><td>172,7 - 182,8</td></tr>
                            <tr><td>42-34</td><td>107 - 110</td><td>121 - 123,7</td><td>182,8 - 193</td></tr>
                            <tr><td>42-36</td><td>107 - 110</td><td>121 - 123,7</td><td>193 - 198,1</td></tr>
                        </tbody>
                    </table>
                </div>
                <p className="pt-3">
                   Пам’ятайте, що посадка джинсів може відрізнятися навіть при однакових замірах талії та довжини. 
                   Щоб дізнатися точну посадку конкретної моделі, обов’язково перегляньте її сторінку. 
                   Також slim та tapered моделі можуть відчуватися щільніше, ніж класичні прямі фасони.
                </p>

                <h2 className="fs-5">Основні моделі чоловічих джинсів Levi’s та особливості посадки</h2>
                <p>
                    Навіть за однакових замірів джинси Levi’s можуть сидіти по-різному -
                    усе залежить від моделі та крою. Нижче наведено короткий опис
                    найпопулярніших чоловічих моделей.
                </p>
                <ModelLinks onlyModels={topModels} />
                <p>Інші моделі Levi’s доступні на сторінках товарів, де можна ознайомитися з фасоном та замірами для точного підбору.</p>
                <p>
                    Щоб правильно підібрати джинси, обов’язково дізнайтеся
                    , <Link className="link-secondary" to="/yak-robyty-zamiry-dzhyns-levis/">як робити заміри джинсів Levi’s</Link> перед покупкою.
                    А якщо хочете перевірити, чи купуєте оригінальні джинси, подивіться нашу
                    статтю <Link className="link-secondary" to="/yak-pereviryty-oryhinalnist-dzhynsiv-levis/">як перевірити оригінальність джинсів Levi’s</Link>.
                </p>
                <p>
                    Оригінальні чоловічі джинси Levi’s можна замовити прямо
                    на <Link className="link-secondary" to="/">головній сторінці магазину</Link>.
                </p>

                <h2 className="fs-5">Чи змінюється розмір джинсів Levi’s після прання</h2>
                <p>
                    Оригінальні джинси Levi’s виготовляються з якісного деніму,
                    однак незначна усадка можлива після першого прання.
                </p>
                <ul>
                    <li>прати при низькій температурі</li>
                    <li>вивертати джинси навиворіт</li>
                    <li>не сушити в сушильній машині</li>
                </ul>
                <p>
                    За правильного догляду більшість моделей зберігають
                    свою початкову посадку.
                </p>

                <h2 className="fs-5">Чому зручно підбирати розмір у {Settings.shopName}</h2>
                <p>
                    <strong>{Settings.shopName}</strong> - український інтернет-магазин,
                    який з 2020 року спеціалізується <strong>виключно на чоловічих джинсах Levi’s</strong>.
                </p>
                <ul>
                    <li>допомагаємо підібрати правильний розмір</li>
                    <li>враховуємо модель, зріст і тип посадки</li>
                    <li>зменшуємо ризик обміну через неправильний розмір</li>
                </ul>

                <h2 className="fs-5">Висновок</h2>
                <p>
                    Розмірна сітка джинсів Levi’s проста, якщо знати, як її читати.
                    Правильні заміри та урахування моделі допоможуть обрати джинси, які ідеально сидітимуть.
                </p>

                <p>
                    Якщо ви шукаєте <strong>оригінальні чоловічі джинси Levi’s в Україні</strong>,
                    інтернет-магазин {Settings.shopName} допоможе зробити правильний вибір.
                </p>
            </div>
             <section className="row mb-3">
                <h2 className="fs-5">Часті питання</h2>
                <Faq items={[
                    {
                        question: "Як правильно обрати розмір джинсів Levi’s?",
                        answer: `Використовуйте таблицю розмірів на сторінці, заміряйте талію та стегна, враховуйте зріст і крій джинсів. Для точності дивіться сторінки конкретних моделей 502, 511, 505, 501 та ін.`
                    },
                    {
                        question: "Як дізнатися свій розмір для конкретної моделі?",
                        answer: "Дивіться таблицю розмірів на цій сторінці та сторінку конкретної моделі для точних замірів."
                    },
                    {
                        question: "Чи змінюється розмір джинсів після прання?",
                        answer: "Можлива невелика усадка після першого прання. Рекомендується прати навиворіт при низькій температурі та не сушити в машині."
                    },
                ]} />
            </section>
            <RecomendedItems items={props.items.filter(x => topModels.includes(String(x.model)))} title="Рекомендуємо джинси Levi’s"/>
        </>
    );
}

export default SizesTable;